/**
 * Personalized Feed Algorithm
 * Main algorithm that orchestrates user profiling, content analysis, and ranking
 */

import { buildUserProfile } from './userProfiler.js';
import { analyzeProductCategories } from './productCategorizer.js';
import { collection, query, where, getDocs, orderBy, limit as firestoreLimit, startAfter } from 'firebase/firestore';
import { db } from '../firebase';

console.log('✅ Personalized Feed Algorithm module loaded successfully');

/**
 * Main personalized feed algorithm
 * @param {string} userId - Current user ID
 * @param {string} sortBy - Current sort preference ('recent' or 'popular')
 * @param {string} activeTab - Current tab ('tutte' or 'following')
 * @param {Array} following - List of users the current user follows
 * @param {number} limit - Number of items to return
 * @returns {Object} - Personalized feed results
 */
export async function getPersonalizedFeed(userId, sortBy = 'recent', activeTab = 'tutte', following = [], limit = 100, cursor = null) {
  try {
    console.log('🎯 Starting personalized feed algorithm for user:', userId);
    
    // Step 1: Get user's preference profile
    const userProfile = await getUserPreferenceProfile(userId);
    console.log('👤 User profile built:', userProfile);
    
    // Step 2: Fetch available wishlists based on tab
    const fetchResult = await fetchAvailableWishlists(activeTab, following, limit, cursor); // Unlimited content fetching with pagination
    console.log('📋 Available wishlists:', fetchResult.wishlists.length);
    
    // Step 3: If user has sufficient profile data, use personalized ranking
    if (userProfile.stats.profileStrength > 0.3) {
      console.log('✨ Using personalized ranking (profile strength:', userProfile.stats.profileStrength + ')');
      const ranking = await getPersonalizedRanking(userProfile, fetchResult.wishlists, userId, sortBy, limit);
      return {
        ...ranking,
        lastDoc: fetchResult.lastDoc,
        hasMore: fetchResult.hasMore
      };
    } else {
      console.log('📊 Using fallback ranking (insufficient profile data)');
      const ranking = getFallbackRanking(fetchResult.wishlists, sortBy, limit);
      return {
        ...ranking,
        lastDoc: fetchResult.lastDoc,
        hasMore: fetchResult.hasMore
      };
    }
    
  } catch (error) {
    console.error('❌ Error in personalized feed algorithm:', error);
    // Fallback to standard sorting
    return await getStandardFeed(sortBy, activeTab, following, limit, cursor);
  }
}

/**
 * Builds user preference profile from their data
 * @param {string} userId - User ID
 * @returns {Object} - User preference profile
 */
async function getUserPreferenceProfile(userId) {
  if (!userId) {
    return { stats: { profileStrength: 0 }, categoryPreferences: {} };
  }

  try {
    // Fetch user's wishlists
    const userWishlistsQuery = query(
      collection(db, 'wishlists'),
      where('userId', '==', userId),
      where('archived', '==', false)
    );
    const userWishlistsSnap = await getDocs(userWishlistsQuery);
    const userWishlists = userWishlistsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch liked wishlists (from user's likes array in other wishlists)
    const likedWishlistsQuery = query(
      collection(db, 'wishlists'),
      where('likes', 'array-contains', userId),
      firestoreLimit(50) // Limit to prevent excessive queries
    );
    const likedWishlistsSnap = await getDocs(likedWishlistsQuery);
    const likedWishlists = likedWishlistsSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Build and return profile
    return buildUserProfile(userWishlists, likedWishlists, []);
    
  } catch (error) {
    console.error('Error building user profile:', error);
    return { stats: { profileStrength: 0 }, categoryPreferences: {} };
  }
}

/**
 * Fetches available wishlists based on tab selection with optimized queries
 * @param {string} activeTab - Current tab
 * @param {Array} following - Following list
 * @param {number} limit - Fetch limit
 * @returns {Array} - Available wishlists
 */
async function fetchAvailableWishlists(activeTab, following, limit, cursor = null) {
  console.log('🔍 fetchAvailableWishlists - activeTab:', activeTab, '| following count:', following.length);
  
  if (activeTab === 'following') {
    if (following.length === 0) {
      console.log('⚠️ Following tab but user not following anyone - returning empty array');
      return [];
    }

    console.log('✅ Following tab with users to follow, using optimized batch queries...');
    
    // 🚀 OPTIMIZED APPROACH: Use batch queries for followed users
    // Split following list into chunks to avoid Firestore 'in' operator limits (max 10)
    const followingChunks = [];
    const chunkSize = 10; // Firestore 'in' operator limit
    
    for (let i = 0; i < following.length; i += chunkSize) {
      followingChunks.push(following.slice(i, i + chunkSize));
    }

    console.log(`🔧 Split ${following.length} followed users into ${followingChunks.length} chunks`);
    
    // Execute batch queries in parallel for better performance
    const batchPromises = followingChunks.map(chunk => {
      const chunkQuery = query(
        collection(db, 'wishlists'),
        where('userId', 'in', chunk),
        where('isPublic', '==', true),
        where('archived', '==', false),
        orderBy('createdAt', 'desc'),
        ...(cursor ? [startAfter(cursor)] : []),
        firestoreLimit(100) // Large batch for unlimited scrolling
      );
      return getDocs(chunkQuery);
    });

    try {
      const batchResults = await Promise.all(batchPromises);
      
      // Combine and flatten results from all batches
      let results = [];
      batchResults.forEach(snapshot => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        results = results.concat(docs);
      });

      // Sort by creation date and limit results
      results.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });

      // Remove slice limit for unlimited content
      // results = results.slice(0, limit);
      
      console.log(`📊 Optimized query retrieved ${results.length} wishlists from followed users`);
      
      // Get cursor for pagination
      const lastDoc = results.length > 0 ? results[results.length - 1] : null;
      
      return {
        wishlists: results,
        lastDoc: lastDoc,
        hasMore: results.length >= limit
      };
      
    } catch (error) {
      console.error('❌ Error in optimized following query, falling back to memory filter:', error);
      
      // Fallback to previous approach if optimized query fails
      return fetchFollowingFallback(following, limit, cursor);
    }
  } else {
    console.log('📋 All/explore tab - fetching all public wishlists');
    // Standard query for all public wishlists
    const wishlistsQuery = query(
      collection(db, 'wishlists'),
      where('isPublic', '==', true),
      where('archived', '==', false),
      orderBy('createdAt', 'desc'),
      ...(cursor ? [startAfter(cursor)] : []),
      firestoreLimit(100) // Unlimited content batch
    );

    const snapshot = await getDocs(wishlistsQuery);
    const results = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📊 Retrieved', results.length, 'public wishlists');
    
    // Get cursor for pagination
    const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return {
      wishlists: results,
      lastDoc: lastDoc,
      hasMore: results.length >= limit
    };
  }
}

/**
 * Fallback method for following tab when optimized query fails
 * @param {Array} following - Following list
 * @param {number} limit - Fetch limit
 * @returns {Array} - Available wishlists
 */
async function fetchFollowingFallback(following, limit, cursor = null) {
  console.log('🔄 Using fallback memory filter approach for following tab');
  
  const wishlistsQuery = query(
    collection(db, 'wishlists'),
    where('isPublic', '==', true),
    where('archived', '==', false),
    orderBy('createdAt', 'desc'),
    ...(cursor ? [startAfter(cursor)] : []),
    firestoreLimit(100) // Large batch for unlimited content
  );
  
  const snapshot = await getDocs(wishlistsQuery);
  let results = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
  
  // Filter by following list in memory
  results = results.filter(wishlist => following.includes(wishlist.userId));
  // Remove slice limit for unlimited content
  // results = results.slice(0, limit);
  
  console.log('📊 Fallback method retrieved', results.length, 'wishlists from followed users');
  
  // Get cursor for pagination
  const lastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
  
  return {
    wishlists: results,
    lastDoc: lastDoc,
    hasMore: results.length >= limit
  };
}

/**
 * Applies personalized ranking to wishlists
 * @param {Object} userProfile - User preference profile
 * @param {Array} wishlists - Available wishlists
 * @param {string} userId - Current user ID
 * @param {string} sortBy - Sort preference
 * @param {number} limit - Return limit
 * @returns {Object} - Ranked wishlists with metadata
 */
async function getPersonalizedRanking(userProfile, wishlists, userId, sortBy, limit) {
  const rankedWishlists = [];
  
  for (const wishlist of wishlists) {
    // Skip user's own wishlists
    if (wishlist.userId === userId) continue;
    
    const score = calculateWishlistScore(wishlist, userProfile, sortBy);
    
    rankedWishlists.push({
      ...wishlist,
      personalizedScore: score.total,
      scoreBreakdown: score.breakdown,
      recommendationReason: generateRecommendationReason(score.breakdown)
    });
  }

  // Sort by personalized score
  rankedWishlists.sort((a, b) => b.personalizedScore - a.personalizedScore);
  
  console.log('🏆 Top recommendations:', rankedWishlists.slice(0, 3).map(w => ({
    name: w.name,
    score: w.personalizedScore,
    reason: w.recommendationReason
  })));

  return {
    wishlists: rankedWishlists, // Return all ranked wishlists for unlimited scrolling
    isPersonalized: true,
    algorithm: 'preference-based',
    hasMore: rankedWishlists.length > 0,
    userProfile: {
      topCategories: Object.entries(userProfile.categoryPreferences || {})
        .sort(([,a], [,b]) => (b.normalizedScore || 0) - (a.normalizedScore || 0))
        .slice(0, 3)
        .map(([category, data]) => ({ category, score: data.normalizedScore })),
      profileStrength: userProfile.stats.profileStrength
    }
  };
}

/**
 * Calculates personalized score for a wishlist
 * @param {Object} wishlist - Wishlist to score
 * @param {Object} userProfile - User preference profile
 * @param {string} sortBy - Sort preference
 * @returns {Object} - Score breakdown
 */
function calculateWishlistScore(wishlist, userProfile, sortBy) {
  const breakdown = {
    categoryMatch: 0,
    recency: 0,
    popularity: 0,
    diversity: 0,
    quality: 0
  };

  // 1. Category Matching (40% weight)
  const wishlistCategories = analyzeProductCategories(wishlist.products || []);
  let categoryScore = 0;
  
  Object.entries(userProfile.categoryPreferences || {}).forEach(([category, preference]) => {
    const wishlistCategoryData = wishlistCategories.distribution[category];
    if (wishlistCategoryData) {
      const match = preference.normalizedScore * wishlistCategoryData.percentage;
      categoryScore += match;
    }
  });
  breakdown.categoryMatch = Math.min(categoryScore * 4, 10); // Scale to 0-10

  // 2. Recency (20% weight)
  const createdAt = wishlist.createdAt?.toDate ? wishlist.createdAt.toDate() : new Date(wishlist.createdAt);
  const daysOld = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
  breakdown.recency = Math.max(0, 10 - daysOld * 0.2); // Decay over time

  // 3. Popularity (20% weight)
  const likesCount = wishlist.likesCount || 0;
  breakdown.popularity = Math.min(Math.log(1 + likesCount) * 2, 10);

  // 4. Content Quality (10% weight)
  const productCount = (wishlist.products || []).length;
  const hasImages = (wishlist.products || []).filter(p => p.image).length;
  const qualityScore = Math.min(productCount * 0.5 + (hasImages / Math.max(productCount, 1)) * 5, 10);
  breakdown.quality = qualityScore;

  // 5. Diversity bonus (10% weight)
  const uniqueCategories = Object.keys(wishlistCategories.distribution).length;
  breakdown.diversity = Math.min(uniqueCategories * 2, 10);

  // Weight the scores based on sort preference
  let weights = { categoryMatch: 0.4, recency: 0.2, popularity: 0.2, quality: 0.1, diversity: 0.1 };
  
  if (sortBy === 'popular') {
    weights = { categoryMatch: 0.3, recency: 0.1, popularity: 0.4, quality: 0.1, diversity: 0.1 };
  } else if (sortBy === 'recent') {
    weights = { categoryMatch: 0.3, recency: 0.4, popularity: 0.1, quality: 0.1, diversity: 0.1 };
  }

  // Calculate weighted total
  const total = Object.entries(breakdown).reduce((sum, [factor, score]) => {
    return sum + (score * weights[factor]);
  }, 0);

  return { total, breakdown, weights };
}

/**
 * Generates human-readable recommendation reason
 * @param {Object} breakdown - Score breakdown
 * @returns {string} - Recommendation reason
 */
function generateRecommendationReason(breakdown) {
  const topFactor = Object.entries(breakdown).reduce((a, b) => 
    breakdown[a[0]] > breakdown[b[0]] ? a : b
  );

  const reasons = {
    categoryMatch: 'Matches your interests',
    recency: 'Recently created',
    popularity: 'Popular with other users',
    quality: 'High-quality content',
    diversity: 'Diverse product selection'
  };

  return reasons[topFactor[0]] || 'Recommended for you';
}

/**
 * Fallback ranking for users with insufficient profile data
 * @param {Array} wishlists - Available wishlists
 * @param {string} sortBy - Sort preference
 * @param {number} limit - Return limit
 * @returns {Object} - Standard sorted wishlists
 */
function getFallbackRanking(wishlists, sortBy, limit) {
  const sortedWishlists = [...wishlists].sort((a, b) => {
    if (sortBy === 'popular') {
      return (b.likesCount || 0) - (a.likesCount || 0);
    } else {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  return {
    wishlists: sortedWishlists, // Return all sorted wishlists for unlimited scrolling
    isPersonalized: false,
    algorithm: 'standard-sort',
    reason: 'Insufficient user data for personalization',
    hasMore: sortedWishlists.length > 0
  };
}

/**
 * Standard feed (backup method)
 * @param {string} sortBy - Sort preference
 * @param {string} activeTab - Active tab
 * @param {Array} following - Following list
 * @param {number} limit - Return limit
 * @returns {Object} - Standard feed
 */
async function getStandardFeed(sortBy, activeTab, following, limit, cursor = null) {
  const fetchResult = await fetchAvailableWishlists(activeTab, following, limit, cursor);
  const ranking = getFallbackRanking(fetchResult.wishlists, sortBy, limit);
  return {
    ...ranking,
    lastDoc: fetchResult.lastDoc,
    hasMore: fetchResult.hasMore
  };
}

/**
 * Analyzes feed performance for a user
 * @param {string} userId - User ID
 * @param {Array} shownWishlists - Wishlists shown to user
 * @param {Array} interactions - User interactions (likes, views, etc.)
 * @returns {Object} - Performance analysis
 */
export function analyzeFeedPerformance(userId, shownWishlists, interactions) {
  const metrics = {
    clickThroughRate: 0,
    likeRate: 0,
    engagementScore: 0,
    categoryAccuracy: 0
  };

  if (shownWishlists.length === 0) return metrics;

  // Calculate click-through rate
  const clicks = interactions.filter(i => i.type === 'click').length;
  metrics.clickThroughRate = clicks / shownWishlists.length;

  // Calculate like rate
  const likes = interactions.filter(i => i.type === 'like').length;
  metrics.likeRate = likes / shownWishlists.length;

  // Overall engagement score
  metrics.engagementScore = (metrics.clickThroughRate * 0.6 + metrics.likeRate * 0.4);

  return metrics;
}

export default {
  getPersonalizedFeed,
  analyzeFeedPerformance
};